import React, {
  memo,
  useCallback,
  useMemo,
  useReducer,
  useEffect,
  useState,
} from 'react';
import { useToast } from './ui/feedback/Toast';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { SearchBar } from './SearchBar';

// Initial state and reducer function
const initialState = {
  isMobileMenuOpen: false,
  spaceMenuOpen: false,
  activeIndex: -1,
};

function headerReducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_MOBILE_MENU':
      return { ...state, isMobileMenuOpen: !state.isMobileMenuOpen };
    case 'TOGGLE_SPACE_MENU':
      return { ...state, spaceMenuOpen: !state.spaceMenuOpen };
    case 'SET_ACTIVE_INDEX':
      return { ...state, activeIndex: action.payload };
    case 'CLOSE_ALL_MENUS':
      return {
        ...state,
        isMobileMenuOpen: false,
        spaceMenuOpen: false,
        activeIndex: -1,
      };
    default:
      return state;
  }
}

function HeaderComponent() {
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const [state, dispatch] = useReducer(headerReducer, initialState);
  const { isMobileMenuOpen, spaceMenuOpen, activeIndex } = state;
  const navigate = useNavigate();

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/');
  }, [signOut, navigate]);

  const handleSearch = useCallback(
    (query) => {
      if (query && query.trim()) {
        navigate(`/search?q=${encodeURIComponent(query)}`);
        dispatch({ type: 'CLOSE_ALL_MENUS' });
      }
    },
    [navigate],
  );

  // Check if user is admin
  const isAdmin = useMemo(() => {
    if (!user) return false;
    return (
      user.app_metadata?.role === 'admin' ||
      user.app_metadata?.role === 'superuser' ||
      user.user_metadata?.is_admin === true
    );
  }, [user]);

  // Space menu sections for clearer browsing (desktop renders grouped, mobile renders a flat list with headings)
  const spaceMenuSections = useMemo(
    () => [
      {
        title: 'Explore',
        items: [
          { to: '/space', label: 'Browse All Space' },
          { to: '/space/nasa', label: 'NASA Data' },
          { to: '/space/nightsky', label: 'Night Sky' },
          { to: '/space/stars', label: 'Star Map' },
          { to: '/space/weather', label: 'Space Weather' },
          { to: '/space/news', label: 'Space News' },
        ],
      },
      {
        title: 'Activities',
        items: [
          { to: '/space/events', label: 'Events' },
          { to: '/space/iss', label: 'ISS Passes' },
          { to: '/space/observations', label: 'Observations' },
          { to: '/space/badges', label: 'Badges' },
          { to: '/space/today', label: 'APOD Today' },
          { to: '/timeline', label: 'Timeline' },
        ],
      },
      {
        title: 'Galleries',
        items: [
          { to: '/space/art', label: 'Art Gallery' },
          { to: '/space/missions', label: 'Missions Gallery' },
          { to: '/space/history', label: 'Rocket City History' },
        ],
      },
    ],
    [],
  );

  // Flattened list used for keyboard navigation
  const menuItems = useMemo(
    () => spaceMenuSections.flatMap((s) => s.items),
    [spaceMenuSections],
  );

  // Memoize event handlers
  const toggleMobileMenu = useCallback(() => {
    if (isMobileMenuOpen) {
      dispatch({ type: 'CLOSE_ALL_MENUS' });
    } else {
      dispatch({ type: 'TOGGLE_MOBILE_MENU' });
    }
  }, [isMobileMenuOpen]);

  const toggleSpaceMenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_SPACE_MENU' });
  }, []);

  const setActiveIndex = useCallback((index) => {
    dispatch({ type: 'SET_ACTIVE_INDEX', payload: index });
  }, []);

  const closeAllMenus = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_MENUS' });
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!spaceMenuOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < menuItems.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : menuItems.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0) {
            closeAllMenus();
            navigate(menuItems[activeIndex].to);
          }
          break;
        case 'Escape':
          closeAllMenus();
          break;
        default:
          break;
      }
    },
    [
      spaceMenuOpen,
      activeIndex,
      menuItems,
      navigate,
      closeAllMenus,
      setActiveIndex,
    ],
  );

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (spaceMenuOpen && !event.target.closest('.space-menu-container')) {
        dispatch({ type: 'TOGGLE_SPACE_MENU' });
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [spaceMenuOpen, setActiveIndex]);

  return (
    <header
      role="banner"
      aria-label="Main site header"
      className="border-b bg-background sticky top-0 z-50 relative"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            {/* Use PNG logo from public directory */}
            <img
              src="/logo.png"
              alt="Rocket City Nerd logo"
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <span className="text-xl font-bold">Rocket City Nerd</span>
          </Link>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-3 lg:gap-4">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={toggleMobileMenu}
              className="lg:hidden inline-flex items-center justify-center p-3 rounded-md text-foreground hover:text-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">
                {isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}
              </span>
              <svg
                className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <nav
              role="navigation"
              aria-label="Primary navigation"
              className="hidden lg:flex min-w-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm"
            >
              <Link
                to="/"
                className="text-foreground hover:text-primary py-3 inline-block"
              >
                Home
              </Link>
              <Link
                to="/blog"
                className="text-foreground hover:text-primary py-3 inline-block"
              >
                Blog
              </Link>
              <Link
                to="/events"
                className="text-foreground hover:text-primary py-3 inline-block"
              >
                Events
              </Link>
              <div
                className="relative group space-menu-container"
                onKeyDown={handleKeyDown}
              >
                <button
                  className="text-blue-400 hover:text-blue-300 transition-colors whitespace-nowrap py-3 inline-block"
                  onClick={toggleSpaceMenu}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSpaceMenu();
                    }
                  }}
                  aria-expanded={spaceMenuOpen}
                  aria-haspopup="true"
                  aria-controls="space-menu"
                >
                  Space
                </button>
                {spaceMenuOpen && (
                  <div className="absolute left-0 top-full z-50 pt-2">
                    <div
                      id="space-menu"
                      role="menu"
                      aria-orientation="vertical"
                      className="min-w-[18rem] md:min-w-[36rem] max-h-[28rem] overflow-y-auto bg-background border rounded-md shadow-lg p-3"
                      onKeyDown={handleKeyDown}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {spaceMenuSections.map((section, sIdx) => (
                          <div key={sIdx} className="min-w-0">
                            <div className="px-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground/80">
                              {section.title}
                            </div>
                            <div className="flex flex-col">
                              {section.items.map((item) => {
                                const index = menuItems.findIndex(
                                  (i) => i.to === item.to,
                                );
                                return (
                                  <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`block px-2 py-2 rounded-md hover:bg-accent hover:text-primary transition-colors ${
                                      activeIndex === index
                                        ? 'bg-accent text-primary'
                                        : ''
                                    }`}
                                    role="menuitem"
                                    onClick={() => {
                                      toggleSpaceMenu();
                                      setActiveIndex(-1);
                                    }}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    tabIndex={0}
                                    aria-selected={activeIndex === index}
                                  >
                                    {item.label}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Link
                to="/toolkit"
                className="text-foreground hover:text-primary py-3 inline-block"
              >
                Toolkit
              </Link>
              <Link
                to="/cheat-sheets"
                className="text-foreground hover:text-primary py-3 inline-block"
              >
                Cheat Sheets
              </Link>
              <Link
                to="/about"
                className="hidden 2xl:inline-block text-foreground hover:text-primary py-3"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="hidden 2xl:inline-block text-foreground hover:text-primary py-3"
              >
                Contact
              </Link>
              <Link
                to="/merch"
                className="hidden 2xl:inline-block text-foreground hover:text-primary py-3"
              >
                Merch
              </Link>
              <Link
                to="/stem"
                className="hidden 2xl:inline-block text-foreground hover:text-primary py-3"
              >
                STEM Directory
              </Link>
              <Link
                to="/subscribe"
                className="hidden xl:inline-block text-foreground hover:text-primary py-3"
              >
                Subscribe
              </Link>
              <Link
                to="/nerd-map"
                className="hidden xl:inline-block text-foreground hover:text-primary py-3"
              >
                🗺️ Nerd Map
              </Link>

              {/* Auth Links */}
              {user ? (
                <div className="flex items-center gap-4">
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="text-foreground hover:text-primary py-3 inline-block"
                    >
                      Admin
                    </Link>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {user.email}
                  </span>
                  <button
                    onClick={handleSignOut}
                    className="text-foreground hover:text-primary py-3 inline-block"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <Link
                    to="/signin"
                    className="text-foreground hover:text-primary py-3 inline-block"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="text-foreground hover:text-primary py-3 inline-block"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </nav>

            <div className="hidden xl:block w-56">
              <SearchBar onSearch={handleSearch} />
            </div>

            {/* Push notifications toggle (desktop) */}
            <div className="hidden lg:block shrink-0">
              <PushToggle />
            </div>
            {/* Theme toggle button */}
            <button
              data-testid="theme-toggle"
              aria-pressed={theme === 'dark'}
              onClick={toggleTheme}
              className="rounded-full p-3 hover:bg-accent"
              aria-label={
                theme === 'dark'
                  ? 'Switch to light mode'
                  : 'Switch to dark mode'
              }
            >
              {theme === 'dark' ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:hidden mt-4 bg-background z-50 w-full absolute left-0 right-0 top-full`}
          id="mobile-menu"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="space-y-1 border-t pt-2">
            <div className="px-3">
              <SearchBar onSearch={handleSearch} />
            </div>
            <Link
              to="/"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Home
            </Link>
            <Link
              to="/blog"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Blog
            </Link>
            <Link
              to="/events"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Events
            </Link>
            <div className="relative space-menu-container">
              <button
                className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-blue-400 hover:text-blue-300 hover:bg-accent whitespace-nowrap"
                onClick={toggleSpaceMenu}
                aria-expanded={spaceMenuOpen}
                aria-haspopup="true"
              >
                Space
              </button>
              {spaceMenuOpen && (
                <div className="pl-4">
                  {spaceMenuSections.map((section, sIdx) => (
                    <div key={sIdx} className="mb-2">
                      <div className="px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground/80">
                        {section.title}
                      </div>
                      {section.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
                          onClick={toggleMobileMenu}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/toolkit"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Toolkit
            </Link>
            <Link
              to="/cheat-sheets"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Cheat Sheets
            </Link>
            <Link
              to="/about"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              About
            </Link>
            <Link
              to="/contact"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Contact
            </Link>
            <Link
              to="/merch"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Merch
            </Link>
            <Link
              to="/stem"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              STEM Directory
            </Link>
            <Link
              to="/subscribe"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              Subscribe
            </Link>
            <Link
              to="/nerd-map"
              className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
              onClick={toggleMobileMenu}
            >
              🗺️ Nerd Map
            </Link>

            {/* Mobile Auth Links */}
            <div className="px-3 pt-2">
              <PushToggle mobile />
            </div>
            {user ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
                    onClick={toggleMobileMenu}
                  >
                    Admin
                  </Link>
                )}
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {user.email}
                </div>
                <button
                  onClick={() => {
                    handleSignOut();
                    toggleMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent whitespace-nowrap"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
                  onClick={toggleMobileMenu}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-accent"
                  onClick={toggleMobileMenu}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export const Header = memo(HeaderComponent);

function PushToggle({ mobile = false }) {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [endpoint, setEndpoint] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    const ok =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setSupported(ok);
    if (!ok) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (sub) {
          setEnabled(true);
          setEndpoint(sub.endpoint);
        }
      });
  }, []);

  const subscribe = useCallback(async () => {
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
      showToast('Missing notification key', 'error');
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      showToast('Notifications blocked', 'error');
      return;
    }
    const reg = await navigator.serviceWorker.ready;
    const key = urlBase64ToUint8Array(vapidPublicKey);
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
    setEnabled(true);
    setEndpoint(sub.endpoint);
    import('../services/pushSubscriptionService').then((m) =>
      m.saveSubscription(sub),
    );
    showToast('Notifications enabled', 'success');
  }, [showToast]);

  const unsubscribe = useCallback(async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const ep = sub.endpoint;
      await sub.unsubscribe();
      import('../services/pushSubscriptionService').then((m) =>
        m.removeSubscription(ep),
      );
    }
    setEnabled(false);
    setEndpoint('');
    showToast('Notifications disabled', 'info');
  }, [showToast]);

  const onClick = useCallback(async () => {
    if (!supported || loading) return;
    setLoading(true);
    try {
      if (enabled) await unsubscribe();
      else await subscribe();
    } finally {
      setLoading(false);
    }
  }, [supported, loading, enabled, unsubscribe, subscribe]);

  const sendTest = useCallback(async () => {
    if (!endpoint) return;
    import('../services/pushSubscriptionService').then((m) =>
      m.sendTestNotification(endpoint),
    );
    showToast('Test notification requested', 'info');
  }, [endpoint, showToast]);

  if (!supported) return null;
  const baseClasses = mobile ? 'w-full mt-2' : '';
  return (
    <div className={`flex items-center gap-2 ${baseClasses}`}>
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={`rounded-full px-4 py-2 text-sm border ${enabled ? 'bg-green-600 text-white border-green-500' : 'bg-gray-700 text-gray-200 border-gray-600'} hover:opacity-90 transition tap-target`}
        aria-pressed={enabled}
      >
        <span aria-hidden={false}>
          {loading ? '...' : enabled ? 'Notifications On' : 'Notify Me'}
        </span>
      </button>
      {enabled && !mobile && (
        <button
          type="button"
          onClick={sendTest}
          className="px-3 py-2 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-500 border border-blue-500 tap-target"
        >
          <span aria-hidden={false}>Send test notification</span>
        </button>
      )}
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
